import React from 'react';
import { clsx } from 'clsx';
import type { TradeTab } from './use-trade-tab';

// Re-export so existing `import { type TradeTab } from './TradeTabNavigation'`
// call sites keep resolving; the canonical home is ./use-trade-tab.
export type { TradeTab } from './use-trade-tab';

const BASE_TABS: { id: TradeTab; label: string }[] = [
  { id: 'analytics', label: 'Economics' },   // D-08 / R10 — label only; route key 'analytics' preserved
  { id: 'activity', label: 'Activity' },
  { id: 'trade-terms', label: 'Trade Terms' },
];

// LR-470: the Recalls tab is appended only when the trade has booked partial
// recalls/returns — no empty tab on the overwhelming majority of trades.
const RECALLS_TAB: { id: TradeTab; label: string } = { id: 'recalls', label: 'Recalls' };

interface TradeTabNavigationProps {
  activeTab: TradeTab;
  onTabChange: (tab: TradeTab) => void;
  /** LR-470: show the conditional Recalls tab (trade has booked partial recalls). */
  showRecalls?: boolean;
}

export function TradeTabNavigation({ activeTab, onTabChange, showRecalls = false }: TradeTabNavigationProps) {
  const tabs = showRecalls ? [...BASE_TABS, RECALLS_TAB] : BASE_TABS;
  return (
    <div className="flex border-b border-lr-border" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          id={`tab-${tab.id}`}
          role="tab"
          aria-selected={activeTab === tab.id}
          // Only reference the panel id while that panel is actually mounted.
          // TradeDetailPage conditionally UNMOUNTS the non-active panels, so a
          // static aria-controls would dangle (WCAG 4.1.2) when the tab is
          // inactive (WR-04). Emit it only for the active tab, whose panel is
          // guaranteed present in the DOM.
          aria-controls={activeTab === tab.id ? `tab-panel-${tab.id}` : undefined}
          onClick={() => onTabChange(tab.id)}
          className={clsx(
            'relative px-4 py-2.5 text-sm font-semibold transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent focus-visible:ring-offset-2 focus-visible:rounded-sm',
            activeTab === tab.id
              ? '-mb-px border-b-2 border-lr-accent text-lr-accent'
              : 'text-lr-muted hover:text-lr-fg-2',
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
